import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ListResponse } from '@lib/api';
import type {
  Talep,
  CreateTalepInput,
  UpdateTalepInput,
  ListTalepQuery,
  CreateYorumInput,
  TalepYorum,
} from '@/types/destek';

const QUERY_KEY = 'talepler';

export function useTalepler(query: ListTalepQuery = {}) {
  return useQuery({
    queryKey: [QUERY_KEY, query],
    queryFn: () =>
      api
        .get('talepler', {
          searchParams: Object.entries(query).reduce(
            (acc, [k, v]) => {
              if (v !== undefined && v !== '') acc[k] = String(v);
              return acc;
            },
            {} as Record<string, string>,
          ),
        })
        .json<ListResponse<Talep>>(),
  });
}

export function useTalep(id: string | undefined) {
  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: () =>
      api
        .get(`talepler/${id}`)
        .json<{ data: Talep }>()
        .then((r) => r.data),
    enabled: !!id,
  });
}

export function useCreateTalep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTalepInput) =>
      api
        .post('talepler', { json: input })
        .json<{ data: Talep }>()
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });
}

export function useUpdateTalep(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateTalepInput) =>
      api
        .patch(`talepler/${id}`, { json: input })
        .json<{ data: Talep }>()
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
      qc.invalidateQueries({ queryKey: [QUERY_KEY, id] });
    },
  });
}

export function useDeleteTalep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`talepler/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });
}

export function useYorumEkle(talepId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateYorumInput) =>
      api
        .post(`talepler/${talepId}/yorum`, { json: input })
        .json<{ data: TalepYorum }>()
        .then((r) => r.data),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: [QUERY_KEY, talepId] }),
  });
}
